  import { useState, useEffect } from 'react';
  import { useNavigate } from 'react-router-dom';

  const API_BASE = process.env.REACT_APP_API_BASE || '/api';

  export default function ScienceExamFlowBuilder() {
    const navigate = useNavigate();
    const [examName, setExamName] = useState('');
    const [availableLevels, setAvailableLevels] = useState([]);
    const [level, setLevel] = useState('');
    const [availableChapters, setAvailableChapters] = useState([]);
    const [selectedChapters, setSelectedChapters] = useState([]);
    const [chapterTree, setChapterTree] = useState([]);
    const [conceptConfigs, setConceptConfigs] = useState({});
    
    const [currentStep, setCurrentStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [debugInfo, setDebugInfo] = useState('Loading levels...');

    useEffect(() => {
      const fetchLevels = async () => {
        try {
          const res = await fetch(`${API_BASE}/admin/science-levels`);
          const data = await res.json();
          setDebugInfo(`API Status: ${res.status} | Response: ${JSON.stringify(data)}`);
          setAvailableLevels(data.levels || []);
        } catch (err) {
          setDebugInfo(`ERROR: ${err.message}`);
        }
      };
      fetchLevels();
    }, []);

    useEffect(() => {
      if (level) {
        fetch(`${API_BASE}/admin/science-chapters?level=${level}`).then(r => r.json()).then(d => {
          setAvailableChapters(d.chapters || []);
          setSelectedChapters([]); setChapterTree([]);
        }).catch(console.error);
      }
    }, [level]);

    const handleChapterToggle = (chapterObj) => {
      const chapterName = chapterObj.chapter;
      if (selectedChapters.includes(chapterName)) {
        setSelectedChapters(selectedChapters.filter(c => c !== chapterName));
        setChapterTree(chapterTree.filter(c => c.chapter !== chapterName));
      } else {
        setSelectedChapters([...selectedChapters, chapterName]);
        const chData = availableChapters.find(c => c.chapter === chapterName);
        if (chData) {
          setChapterTree([...chapterTree, chData]);
          const newConfigs = { ...conceptConfigs };
          chData.concepts.forEach(concept => {
            const key = `${chapterName}|${concept.concept_name}`;
            if (!newConfigs[key]) {
              newConfigs[key] = {
                core_upper_threshold: 80, core_lower_threshold: 50, backup_threshold: 70,
                core_questions_per_subconcept: 1, backup_questions_per_subconcept: 1, supporting_questions_per_subconcept: 1
              };
            }
          });
          setConceptConfigs(newConfigs);
        }
      }
    };

    const updateConceptConfig = (key, field, value) => {
      setConceptConfigs(prev => ({ ...prev, [key]: { ...prev[key], [field]: Number(value) } }));
    };

    const handleCreateExam = async () => {
      setLoading(true); setError('');
      try {
        if (!examName) throw new Error('Exam name is required');
        if (selectedChapters.length === 0) throw new Error('Select at least one chapter');

        const payloadChapters = chapterTree.map(ch => ({
          chapter: ch.chapter,
          chapter_name: ch.chapter_name,
          concepts: ch.concepts.map(c => ({
            concept_name: c.concept_name,
            sub_concepts: c.sub_concepts,
            config: conceptConfigs[`${ch.chapter}|${c.concept_name}`]
          }))
        }));

        const res = await fetch(`${API_BASE}/admin/science-exam`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
          body: JSON.stringify({ exam_name: examName, level, chapters: payloadChapters })
        });

        if (!res.ok) throw new Error((await res.json()).error);
        alert('✅ Science Exam created!');
        navigate('/admin/flow-builder-exams');
      } catch (err) { setError(err.message); } 
      finally { setLoading(false); }
    };

    return (
      <div style={{ maxWidth: 1200, margin: '40px auto', padding: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
          <h2 style={{ margin: 0 }}>🔬 Create Science Diagnostic Exam</h2>
          <button onClick={() => navigate('/admin/flow-builder-exams')} style={{ padding: '10px 16px', background: '#6c757d', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}>✕ Cancel</button>
        </div>

        <div style={{ padding: 10, background: '#fff3cd', border: '1px solid #ffeeba', borderRadius: 6, marginBottom: 20, fontSize: 13, fontFamily: 'monospace' }}>
          <strong>🔍 Debug Info:</strong> {debugInfo}
        </div>

        {error && <div style={{ padding: 12, background: '#f8d7da', color: '#721c24', borderRadius: 6, marginBottom: 15 }}>❌ {error}</div>}

        <div style={{ display: 'flex', gap: 10, marginBottom: 30 }}>
          {[1, 2].map(step => (
            <div key={step} style={{ flex: 1, padding: 12, background: currentStep >= step ? '#007BFF' : '#e9ecef', color: currentStep >= step ? '#fff' : '#666', borderRadius: 6, textAlign: 'center', fontWeight: 600 }}>
              Step {step}: {step === 1 ? 'Select Chapters' : 'Configure Concepts'}
            </div>
          ))}
        </div>

        {currentStep === 1 && (
          <div style={{ background: '#fff', padding: 20, borderRadius: 8, border: '1px solid #ddd' }}>
            <h3>Basic Info</h3>
            <input type="text" value={examName} onChange={e => setExamName(e.target.value)} placeholder="Exam Name" style={{ width: '100%', padding: 10, marginBottom: 15, borderRadius: 4, border: '1px solid #ccc' }} />
            
            <label style={{fontWeight: 500}}>Select Level</label>
            <select value={level} onChange={e => setLevel(e.target.value)} style={{ width: '100%', padding: 10, marginBottom: 15, borderRadius: 4, border: '1px solid #ccc' }}>
              <option value="">-- Select Level --</option>
              {availableLevels.map(l => <option key={l} value={l}>Level {l}</option>)}
            </select>

            {level && (
              <>
                <h4>Select Chapters</h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: 10, marginBottom: 20 }}>
                  {availableChapters.map(ch => (
                    <label key={ch.chapter} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 12, background: selectedChapters.includes(ch.chapter) ? '#e7f1ff' : '#f8f9fa', border: '1px solid #ddd', borderRadius: 6, cursor: 'pointer' }}>
                      <input type="checkbox" checked={selectedChapters.includes(ch.chapter)} onChange={() => handleChapterToggle(ch)} />
                      <div>
                        <div style={{fontWeight: 600}}>Chapter {ch.chapter}</div>
                        <div style={{fontSize: 12, color: '#666'}}>{ch.chapter_name}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </>
            )}
            
            <button onClick={() => setCurrentStep(2)} disabled={selectedChapters.length === 0} style={{ padding: '12px 24px', background: '#007BFF', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}>
              Continue to Concept Config →
            </button>
          </div>
        )}

        {currentStep === 2 && (
          <div style={{ background: '#fff', padding: 20, borderRadius: 8, border: '1px solid #ddd' }}>
            <h3>Configure Concepts & Thresholds</h3>
            {chapterTree.map(ch => (
              <div key={ch.chapter} style={{ marginBottom: 30 }}>
                <h4 style={{ borderBottom: '2px solid #007BFF', paddingBottom: 5 }}>📚 Chapter {ch.chapter}: {ch.chapter_name}</h4>
                {ch.concepts.map(concept => {
                  const key = `${ch.chapter}|${concept.concept_name}`;
                  const cfg = conceptConfigs[key] || {};
                  return (
                    <div key={key} style={{ padding: 15, background: '#f8f9fa', borderRadius: 8, marginBottom: 15, border: '1px solid #dee2e6' }}>
                      <h5 style={{ marginTop: 0, color: '#0056b3' }}>🧠 {concept.concept_name}</h5>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 15 }}>
                        <div><label style={{fontSize: 12, fontWeight: 600}}>Core Pass %</label><input type="number" value={cfg.core_upper_threshold || 0} onChange={e => updateConceptConfig(key, 'core_upper_threshold', e.target.value)} style={{width: '100%', padding: 6}} /></div>
                        <div><label style={{fontSize: 12, fontWeight: 600}}>Core Fail %</label><input type="number" value={cfg.core_lower_threshold || 0} onChange={e => updateConceptConfig(key, 'core_lower_threshold', e.target.value)} style={{width: '100%', padding: 6}} /></div>
                        <div><label style={{fontSize: 12, fontWeight: 600}}>Backup Pass %</label><input type="number" value={cfg.backup_threshold || 0} onChange={e => updateConceptConfig(key, 'backup_threshold', e.target.value)} style={{width: '100%', padding: 6}} /></div>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                        <div><label style={{fontSize: 12, fontWeight: 600}}>Core Qs/Sub</label><input type="number" value={cfg.core_questions_per_subconcept || 0} onChange={e => updateConceptConfig(key, 'core_questions_per_subconcept', e.target.value)} style={{width: '100%', padding: 6}} /></div>
                        <div><label style={{fontSize: 12, fontWeight: 600}}>Backup Qs/Sub</label><input type="number" value={cfg.backup_questions_per_subconcept || 0} onChange={e => updateConceptConfig(key, 'backup_questions_per_subconcept', e.target.value)} style={{width: '100%', padding: 6}} /></div>
                        <div><label style={{fontSize: 12, fontWeight: 600}}>Supporting Qs/Sub</label><input type="number" value={cfg.supporting_questions_per_subconcept || 0} onChange={e => updateConceptConfig(key, 'supporting_questions_per_subconcept', e.target.value)} style={{width: '100%', padding: 6}} /></div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setCurrentStep(1)} style={{ padding: '12px 24px', background: '#6c757d', color: '#fff', border: 'none', borderRadius: 6 }}>← Back</button>
              <button onClick={handleCreateExam} disabled={loading} style={{ padding: '12px 24px', background: '#28a745', color: '#fff', border: 'none', borderRadius: 6 }}>{loading ? 'Creating...' : '✅ Create Science Exam'}</button>
            </div>
          </div>
        )}
      </div>
    );
  }